import { Check, CheckSquare, RotateCcw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import type { DueReview, SourceNode } from "../types";
import { MarkdownPreview } from "./MarkdownPreview";

type Props = {
  reviews: DueReview[];
  sources: SourceNode[];
  loading?: boolean;
  onReviewed: (objectId: string) => Promise<void>;
};

function sinceLabel(iso: string) {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) {
    return "";
  }
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return "hôm nay";
  if (days === 1) return "1 ngày trước";
  if (days < 30) return `${days} ngày trước`;
  const months = Math.floor(days / 30);
  return `${months} tháng trước`;
}

export function ReviewPanel({ reviews, sources, loading, onReviewed }: Props) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [reviewingIds, setReviewingIds] = useState<Set<string>>(() => new Set());
  const [message, setMessage] = useState("");
  const sourceById = useMemo(() => new Map(sources.map((source) => [source.id, source])), [sources]);

  const sourcePath = (id: string) => {
    const source = sourceById.get(id);
    if (!source) {
      return "";
    }

    const path = [source.title];
    let parentId = source.parentId ?? "";
    const seen = new Set<string>();

    while (parentId && !seen.has(parentId)) {
      seen.add(parentId);
      const parent = sourceById.get(parentId);
      if (!parent) {
        break;
      }
      path.unshift(parent.title);
      parentId = parent.parentId ?? "";
    }

    return path.join(" / ");
  };

  const visibleIds = useMemo(() => new Set(reviews.map((item) => item.id)), [reviews]);
  const selectedVisibleIds = useMemo(() => [...selectedIds].filter((id) => visibleIds.has(id)), [selectedIds, visibleIds]);

  useEffect(() => {
    setSelectedIds((current) => {
      const next = new Set([...current].filter((id) => visibleIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [visibleIds]);

  const toggleSelected = (id: string) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const markReviewed = async (ids: string[]) => {
    if (ids.length === 0) {
      return;
    }

    setReviewingIds((current) => new Set([...current, ...ids]));
    setMessage("");

    try {
      for (const id of ids) {
        await onReviewed(id);
      }
      setSelectedIds((current) => {
        const next = new Set(current);
        ids.forEach((id) => next.delete(id));
        return next;
      });
      setMessage(ids.length === 1 ? "Đã đánh dấu 1 note đã ôn." : `Đã đánh dấu ${ids.length} note đã ôn.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Không đánh dấu ôn tập được.");
    } finally {
      setReviewingIds((current) => {
        const next = new Set(current);
        ids.forEach((id) => next.delete(id));
        return next;
      });
    }
  };

  return (
    <div className="space-y-4">
      <section className="rounded-lg border border-line bg-white shadow-panel">
        <div className="border-b border-line px-4 py-3">
          <h1 className="inline-flex items-center gap-2 text-lg font-bold">
            <RotateCcw size={18} aria-hidden="true" /> Ôn tập hôm nay
          </h1>
          <p className="text-sm font-medium text-muted">Những note lâu chưa xem lại — ôn nhanh để nhớ lâu.</p>
        </div>

        <div className="p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <p className="text-xs font-semibold text-muted">
              {reviews.length} note trong queue · {selectedVisibleIds.length} đã chọn
            </p>
            <button
              className="inline-flex h-9 items-center gap-2 rounded-lg bg-brand px-3 text-xs font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              type="button"
              disabled={selectedVisibleIds.length === 0 || reviewingIds.size > 0}
              onClick={() => void markReviewed(selectedVisibleIds)}
            >
              <CheckSquare size={15} aria-hidden="true" />
              {reviewingIds.size > 0 ? "Đang lưu" : "Đánh dấu đã ôn"}
            </button>
          </div>
          <p className="sr-only" aria-live="polite">
            {message}
          </p>
          {message ? <p className="mb-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2 text-xs font-semibold text-brand">{message}</p> : null}
          {loading ? <p className="text-sm text-muted">Đang tải...</p> : null}
          {!loading && reviews.length === 0 ? (
            <div className="rounded-lg border border-dashed border-line px-4 py-10 text-center">
              <Check size={26} className="mx-auto text-emerald-400" aria-hidden="true" />
              <p className="mt-2 text-sm font-medium text-slate-600">Chưa có gì cần ôn — quay lại sau nhé.</p>
            </div>
          ) : null}

          <ul className="space-y-3">
            {reviews.map((item) => (
              <li key={item.id} className="rounded-lg border border-line bg-white p-3">
                <div className="flex items-start gap-3">
                  <input
                    id={`review-${item.id}`}
                    className="mt-1 h-4 w-4 rounded border-line text-brand focus:ring-2 focus:ring-blue-500"
                    type="checkbox"
                    checked={selectedIds.has(item.id)}
                    disabled={reviewingIds.has(item.id)}
                    onChange={() => toggleSelected(item.id)}
                  />
                  <div className="min-w-0 flex-1">
                    <label className="block cursor-pointer text-sm font-bold text-ink" htmlFor={`review-${item.id}`}>
                      {item.title}
                    </label>
                    <p className="mt-1 truncate text-xs font-semibold text-muted">
                      {sourcePath(item.sourceId) ? `${sourcePath(item.sourceId)} · ` : ""}
                      {item.kind} · {sinceLabel(item.updatedAt)}
                      {item.reviewCount > 0 ? ` · đã ôn ${item.reviewCount} lần` : ""}
                    </p>
                    <div className="mt-3 rounded-lg border border-line bg-slate-50 px-3 py-2">
                      <MarkdownPreview content={item.content || item.title} compact />
                    </div>
                  </div>
                  <button
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 text-xs font-bold text-emerald-700 hover:bg-emerald-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-muted"
                    type="button"
                    disabled={reviewingIds.has(item.id)}
                    onClick={() => void markReviewed([item.id])}
                  >
                    <Check size={14} aria-hidden="true" /> {reviewingIds.has(item.id) ? "Đang lưu" : "Đã ôn"}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
